/**
 * fuarKurulumSaatiService.js — Kurulum (setup) hours per role (Katılımcı / Dekoratör / Tedarikçi) and per company (backlog #718,
 * the frontend half of backend #717). Contract: FDS-Backend guides/FE-FuarKurulumSaatiController-guide.md and api-endpoints.md >
 * FuarKurulumSaatiController. CANLI (no mock).
 *
 * THE RULE LIVES IN THE BACKEND and is not repeated here. Per bound (start and end apart) a company is told: its own window at the fair,
 * else its roles' (a role with no window uses the fair's default; the earliest start and the latest end win), else the fair's Kurulum.
 * This file only reads and writes; the window to PRINT always comes from GET /fuar/{fuarId}/firmalar/{firmaId}/kurulum-saati
 * (getFirmaKurulumSaati), never from the fair row.
 *
 * A "pencere" (window) everywhere is { baslangicTarihi, baslangicSaati, bitisTarihi, bitisSaati }: dates yyyy-MM-dd, times sent as HH:mm
 * and answered as HH:mm:ss, every bound optional (a missing time = start of the day for a start, end of the day for an end).
 */
import axios from "axios";
import { JAVA_BACKEND_API_URL, getAuthHeader } from "../Components/Contexts/BackendConstants";

const FUAR_API = `${JAVA_BACKEND_API_URL}/fuar`;
const jsonBasliklar = () => ({ ...getAuthHeader(), "Content-Type": "application/json" });

/** The four bounds of a window; the order is the order they are sent. */
export const PENCERE_ALANLARI = ["baslangicTarihi", "baslangicSaati", "bitisTarihi", "bitisSaati"];

/** Where a bound of a company's window comes from (kaynak): the company's own window, a role's, or the fair's own Kurulum. */
export const KURULUM_KAYNAKLARI = { FIRMA: "FIRMA", ROL: "ROL", FUAR: "FUAR" };

/** "09:00:00" → "09:00" (what <input type="time"> shows); null / "" → "". */
export const saatKisalt = (v) => (typeof v === "string" && /^\d{2}:\d{2}/.test(v) ? v.slice(0, 5) : "");

/** "2026-11-03" or "2026-11-03 14:40:00" → "2026-11-03"; anything else → "". */
export const tarihKisalt = (v) => (typeof v === "string" && /^\d{4}-\d{2}-\d{2}/.test(v) ? v.slice(0, 10) : "");

/** A window as the form keeps it: all four bounds as strings, "" when empty (dates yyyy-MM-dd, times HH:mm). */
export function pencereFormu(p) {
  return {
    baslangicTarihi: tarihKisalt(p?.baslangicTarihi),
    baslangicSaati: saatKisalt(p?.baslangicSaati),
    bitisTarihi: tarihKisalt(p?.bitisTarihi),
    bitisSaati: saatKisalt(p?.bitisSaati),
  };
}

/** A role row of the fair form: the window plus who the role is. `rolAdi` comes from the backend in the request language. */
export function rolFormu(r) {
  return { firmaTypeId: Number(r.firmaTypeId), rolAdi: r.rolAdi || "", tanimli: Boolean(r.tanimli), ...pencereFormu(r) };
}

/** A time without its date is refused by the backend; so is an end before its start. Returns null, "tarihsiz" or "sira" (the same two refusals, caught before the fair is saved). */
export function pencereSorunu(p) {
  if ((p.baslangicSaati && !p.baslangicTarihi) || (p.bitisSaati && !p.bitisTarihi)) return "tarihsiz";
  if (p.baslangicTarihi && p.bitisTarihi) {
    const bas = `${p.baslangicTarihi}T${p.baslangicSaati || "00:00"}`;
    const bit = `${p.bitisTarihi}T${p.bitisSaati || "23:59"}`;
    if (bit < bas) return "sira";
  }
  return null;
}

const ayniPencere = (a, b) => PENCERE_ALANLARI.every((alan) => (a?.[alan] || "") === (b?.[alan] || ""));

/**
 * The request body of PUT /fuar/{id}/kurulum-saatleri for the roles whose window CHANGED against what was loaded: a role listed gets
 * exactly its four bounds (all empty = back to the fair's default), a role left out is kept — so an untouched role is not written again.
 * [] when nothing changed (then nothing is sent).
 */
export function degisenRoller(kayitli, simdiki) {
  const onceki = new Map((kayitli || []).map((r) => [Number(r.firmaTypeId), r]));
  return (simdiki || [])
    .filter((r) => !ayniPencere(onceki.get(Number(r.firmaTypeId)), r))
    .map((r) => ({ firmaTypeId: Number(r.firmaTypeId), ...Object.fromEntries(PENCERE_ALANLARI.map((alan) => [alan, r[alan] || ""])) }));
}

/** GET /fuar/{fuarId}/kurulum-saatleri (staff) → { fuarId, varsayilan, roller: [{ firmaTypeId, rolKey, rolAdi, tanimli, ...pencere }], firmalar }. */
export async function getFuarKurulumSaatleri(fuarId) {
  const res = await axios.get(`${FUAR_API}/${fuarId}/kurulum-saatleri`, { headers: getAuthHeader() });
  return res.data;
}

/**
 * PUT /fuar/{fuarId}/kurulum-saatleri (page.fuar), after the fair itself is saved. `roller` = [{ firmaTypeId, ...four bounds }]
 * (see degisenRoller). A 400 names the role in the request language and saves nothing, also not the other roles.
 */
export async function saveFuarKurulumSaatleri(fuarId, roller) {
  const res = await axios.put(`${FUAR_API}/${fuarId}/kurulum-saatleri`, { roller }, { headers: jsonBasliklar() });
  return res.data;
}

/**
 * GET /fuar/{fuarId}/firmalar/{firmaId}/kurulum-saati — the window this company is told at this fair, where each bound comes from, the
 * company's own window (firmaSaati), its roles there (roller[].ad / .pencere) and the fair's default (varsayilan). Staff any company,
 * a company login its own.
 */
export async function getFirmaKurulumSaati(fuarId, firmaId) {
  const res = await axios.get(`${FUAR_API}/${fuarId}/firmalar/${firmaId}/kurulum-saati`, { headers: getAuthHeader() });
  return res.data;
}

/** PUT the same path (page.firma): the company's own window becomes exactly `pencere` (an empty bound = taken from its roles, all empty = removed). Answers the fresh GET. */
export async function saveFirmaKurulumSaati(fuarId, firmaId, pencere) {
  const govde = Object.fromEntries(PENCERE_ALANLARI.map((alan) => [alan, pencere?.[alan] || ""]));
  const res = await axios.put(`${FUAR_API}/${fuarId}/firmalar/${firmaId}/kurulum-saati`, govde, { headers: jsonBasliklar() });
  return res.data;
}

/** DELETE the same path (page.firma): the company goes back to its roles' window. Answers the fresh GET. */
export async function deleteFirmaKurulumSaati(fuarId, firmaId) {
  const res = await axios.delete(`${FUAR_API}/${fuarId}/firmalar/${firmaId}/kurulum-saati`, { headers: getAuthHeader() });
  return res.data;
}

const sinir = (tarih, saat, yedek) => (tarih ? { tarih: tarihKisalt(tarih), saat: saatKisalt(saat) || null } : (yedek || { tarih: null, saat: null }));

/**
 * The "kurulum" phase the İSG Taahhütnamesi prints, in the shape of Components/Fairs/fuarAsamalari.js ({ anahtar, simge, ilk: { tarih, saat }, son: { tarih, saat } },
 * `saat` "HH:mm" or null), made from the window one company is told. A bound the backend knows nothing about (all null) keeps the fair's own value
 * (`fuarKurulum`), so the form never prints an empty cell where the fair has dates; null when neither knows a date.
 */
export function kurulumAsamasi(firmaPenceresi, fuarKurulum) {
  const ilk = sinir(firmaPenceresi?.baslangicTarihi, firmaPenceresi?.baslangicSaati, fuarKurulum?.ilk);
  const son = sinir(firmaPenceresi?.bitisTarihi, firmaPenceresi?.bitisSaati, fuarKurulum?.son);
  if (!ilk.tarih && !son.tarih) return null;
  return { anahtar: "kurulum", simge: fuarKurulum?.simge || "bx-wrench", ilk, son };
}
